import { getClientApi, getExecutorApi } from '@/core/api/generatedApiClient';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import { clientService } from '@/entities/Client/api/clientService';
import { executorService } from '@/entities/Executor/api/executorService';
import type { ChatRole } from '@/entities/Chat/model/types';
import type { UserChatCompanionProfile, UserChatDirectoryPage, UserChatProfile } from '../model/types';

/** Находит профили участников переписок в админке */
export const userChatProfileService = {
    /** Загружает пользователей выбранной роли для общей сортировки переписок */
    async getDirectoryProfiles(role: ChatRole): Promise<UserChatProfile[]> {
        const profiles: UserChatProfile[] = [];
        let totalCount = Number.POSITIVE_INFINITY;
        let skipCount = 0;

        while (skipCount < totalCount) {
            const page = await userChatProfileService.getPage(role, 100, skipCount);
            profiles.push(...page.profiles);
            totalCount = page.totalCount;
            skipCount += page.profiles.length;

            if (page.profiles.length === 0) break;
        }

        return [...new Map(profiles.map((selection) => [selection.profile.id, selection])).values()];
    },

    /** Загружает страницу клиентов или исполнителей */
    async getPage(
        role: ChatRole,
        takeCount: number,
        skipCount: number
    ): Promise<UserChatDirectoryPage> {
        if (role === 'Client') {
            const response = await clientService.getList({
                takeCount,
                skipCount
            });

            return {
                profiles: (response.clients ?? []).map((profile) => ({
                    role,
                    profile
                })),
                totalCount: response.allCount ?? 0,
            };
        }

        const response = await executorService.getList({
            takeCount,
            skipCount
        });

        return {
            profiles: (response.executors ?? []).map((profile) => ({
                role,
                profile
            })),
            totalCount: response.allCount ?? 0,
        };
    },

    /** Загружает выбранный профиль по ссылке */
    async getById(profileId: string, role: ChatRole): Promise<UserChatProfile> {
        return role === 'Client'
            ? {
                role,
                profile: await clientService.getById(profileId)
            }
            : {
                role,
                profile: await executorService.getById(profileId)
            };
    },

    /** Находит профиль и телефон собеседника по его учётной записи */
    async getCompanionProfile(
        appUserId: string,
        role: ChatRole
    ): Promise<UserChatCompanionProfile | null> {
        try {
            const response = role === 'Client'
                ? await getClientApi().getClientByAppUserId({ appUserId })
                : await getExecutorApi().getExecutorByAppUserId({ appUserId });

            const profileId = response.data.id;

            return profileId
                ? {
                    url: `/${role === 'Client'
                        ? 'clients'
                        : 'executors'}/${encodeURIComponent(profileId)}`,
                    phone: response.data.phone?.trim() || null,
                }
                : null;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },
};
